// Appliance robots. Server-only brains; clients see them through snapshots.
// Every attack has a telegraph carried by the appliance itself (toast glows, fan spins up,
// lever cranks, scope glints, drum spins) that lasts longer than any sane round trip.

import { DT, HISTORY_TICKS, Rng, clamp, len2, secs, wrapAngle } from './core';
import { DISRUPTOR, DTactic, ENEMIES, ENGINEER, ETactic, EType, PLAYER_RADIUS } from './defs';
import { BKind, BULLETS, HitSrc, lobVelocity } from './bullets';
import { Move, PlayerState, isHidden, isTargetable } from './player';
import { AEGIS_DELAY_SECS, Affix, EnemyDef, LIMP_MUL, HELI_HEAL, HELI_HEAL_R, HELI_ORBIT, HELI_SHIELD, HELI_SHIELD_REGEN, HELI_SPRAY_COOL, HELI_SPRAY_DUR, HELI_SPRAY_R, HELI_SPRAY_SECS, HELI_WARD_RANGE, HOVER_ALT, HOVER_CLIMB, HOVER_THRUST, REST_DESCENT, REST_IN_MAX, REST_IN_MIN, REST_MAX, REST_MIN, isBossType, isFlier, MUT_SILENT, SEVER_HP, TURRET_FIRE_SECS, TURRET_LIFE_SECS, TURRET_RANGE, TURRET_DEPLOY_SECS, CAPTAIN_BATCH, CAPTAIN_RING, CAPTAIN_SPAWN_SECS, STALK_CD, STALK_DMG, STALK_FADE_MUL, STALK_KNOCK, STALK_ORBIT, STALK_PATIENCE, STALK_REACH, STALK_REAR_COS, STALK_RETREAT, STALK_REVEAL, STALK_SPEED, STALK_STAGGER, STALK_TACTIC_JITTER, STALK_TACTIC_SECS } from './defs';
import { iceSlowMul, tickEnemyElems } from './elements';
import { Elem } from './weapons';
import { NO_GROUND, World } from './world';
import { PERCH, PERCH_RULES, PerchBook, PerchLevel, arcClear, arcFlight, perchLevel, ringSpot } from './perch';

export const enum EState { Chase = 0, Windup = 1, Attack = 2, Recover = 3, Stun = 4, Spawn = 5 }

export interface Enemy {
  id: number;
  type: EType;
  x: number; y: number; z: number;
  vx: number; vy: number; vz: number;
  kx: number; kz: number; // knockback velocity
  yaw: number;
  hp: number; maxHp: number;
  shield: number; maxShield: number;
  armor: number;
  st: EState; stT: number; stDur: number;
  mode: number; // sub-attack
  target: number; retargetT: number;
  atkCd: number;
  aimX: number; aimY: number; aimZ: number;
  strafe: number;
  primedBy: number; primeT: number; primeStunCd: number;
  stunT: number; burnT: number; burnOwner: number; vulnT: number; stripT: number;
  /** weight (defs.ts STAGGER_*): reeling from a hit that beat its stagger resistance (2x damage); the per-round micro-flinch */
  staggerT: number; flinchT: number;
  /** the Phantom warped through this robot: that cowboy's next strike counts as a backstab for graceT (CLASSES.md) */
  graceBy: number; graceT: number;
  // the elements (sim/elements.ts): burn rate, ice stacks and their slow, frozen solid, armour tiers stripped, the magnetic
  // stagger cut and its clock, wet, the element it carries (armor_element: Elem or None), and when each element last landed
  burnDps: number; iceN: number; iceSlow: number; frozenT: number; stripN: number; magCut: number; magT: number; wetT: number; armorEl: number; elUntil: number[];
  /** Prospector's Lens (sim/cards.ts): who pinged it and for how long their hits land 1.1x */
  lensBy: number; lensT: number;
  /** the roster lane (ROSTER.md): the elite affix, the Aegis refill clock, the Redshell still up; the Stalker's reveal clock
   *  (cloaked while 0), tactic and its clock, the post-strike retreat, orbit side / patience / fade side; the Swarm Captain
   *  (big = 1) and its brood clock; who it is the brood of; routing (fleeing to sink, loot-free); which wave it came in with;
   *  an anchor (its death routs the wave's chaff); the element level an elemental robot procs at */
  affix: number; aegisT: number; redshell: number; revealT: number; retreatT: number; circleSign: number; patienceT: number; fadeSign: number;
  big: number; broodT: number; broodOf: number; routT: number; wave: number; anchor: number; elLevel: number;
  /** a gold bounty robot (CHASE.md): a golden ticket when it falls */
  gold: number;
  /** dismantling (ROSTER.md): the gun router's pool (Shooter / Sniper; 0 = severed), panels shed (Brute), the shed cooldown,
   *  the Engineer's deploy clock, the turret's life and fire clock */
  gunHp: number; shed: number; shedCd: number; deployT: number; lifeT: number;
  /** the fliers (ROSTER.md FLYERS): hover height above the ground (0 = grounded), the rest cycle (ticks to the next rest, rest
   *  left; resting = jets off on the ground), who a helicopter wards, its spray clock and spray left */
  hoverAlt: number; restIn: number; restT: number; resting: number; ward: number; sprayT: number; spraying: number;
  /** visible damage (playtest 3): damage taken on each part, the parts shot off (bit per defs.ts Part), limping (legs gone) */
  partDmg: number[]; parts: number; limp: number;
  /** ice / time-grenade slow: half speed while > 0 (weapons lane) */
  slowT: number;
  hyper: number;
  fleeT: number;
  airborne: number;
  /** 1 = a named nemesis (sim/nemesis.ts): more hull, harder hits */
  nemesis: number;
  shieldRegenT: number;
  lastHitBy: number;
  hitOnce: number; // attack already connected this swing
  // support robots (Disruptor / Engineer): rolling tactic, throttled ally scan, flee heading
  tactic: number; tacticT: number; scanT: number;
  ally: number; // Disruptor: 1 = wounded-ally centroid valid; Engineer: repair patient id; -1 none
  allyX: number; allyZ: number; allyLos: number;
  fleeX: number; fleeZ: number;
  // perch counters (sim/perch.ts): a spot that can see / leap onto a high cowboy, and its rescan clock
  perchX: number; perchZ: number; perchOk: number; perchScanT: number;
  /** Swarmer: ticks until it can leap again */
  hopCd: number;
  dead: boolean;
  hx: Float32Array; hy: Float32Array; hz: Float32Array; hyaw: Float32Array; // lag comp history
}

export function makeEnemy(id: number, type: EType, x: number, y: number, z: number, tierMul: number, rng: Rng): Enemy {
  const d = ENEMIES[type];
  return {
    id, type, x, y, z, vx: 0, vy: 0, vz: 0, kx: 0, kz: 0, yaw: rng.range(-Math.PI, Math.PI),
    hp: d.hp * tierMul, maxHp: d.hp * tierMul, shield: d.shield * tierMul, maxShield: d.shield * tierMul, armor: d.armor,
    st: EState.Spawn, stT: 0, stDur: secs(0.6), mode: 0,
    target: -1, retargetT: 0, atkCd: secs(rng.range(0.8, 2.0)), aimX: 0, aimY: 0, aimZ: 0, strafe: rng.chance(0.5) ? 1 : -1,
    primedBy: -1, primeT: 0, primeStunCd: 0, stunT: 0, burnT: 0, burnOwner: -1, vulnT: 0, stripT: 0, slowT: 0, staggerT: 0, flinchT: 0, graceBy: -1, graceT: 0,
    burnDps: 6, iceN: 0, iceSlow: 0, frozenT: 0, stripN: 1, magCut: 0, magT: 0, wetT: 0, armorEl: Elem.None, elUntil: [0, 0, 0, 0, 0], lensBy: -1, lensT: 0,
    affix: 0, aegisT: 0, redshell: 0, revealT: 0, retreatT: 0, circleSign: 1, patienceT: 0, fadeSign: 1, big: 0, broodT: 0, broodOf: -1, routT: 0, wave: 0, anchor: 0, elLevel: 1, gold: 0,
    gunHp: type === EType.Shooter || type === EType.Sniper ? SEVER_HP : 0, shed: 0, shedCd: 0, deployT: type === EType.Engineer ? secs(4) : 0, lifeT: type === EType.Turret ? secs(TURRET_LIFE_SECS) : 0,
    hoverAlt: HOVER_ALT[type] ?? 0, restIn: HOVER_ALT[type] ? secs(rng.range(REST_IN_MIN, REST_IN_MAX)) : 0, restT: 0, resting: 0, ward: -1, sprayT: secs(HELI_SPRAY_SECS), spraying: 0,
    partDmg: [0, 0, 0, 0], parts: 0, limp: 0,
    hyper: type === EType.MetalBrute ? 1 : 0, fleeT: 0, airborne: 0, nemesis: 0, shieldRegenT: 0, lastHitBy: -1, hitOnce: 0,
    tactic: type === EType.Disruptor ? rollDisruptorTactic(rng, -1) : type === EType.Engineer ? rollEngineerTactic(rng, -1) : 0,
    tacticT: type === EType.Disruptor || type === EType.Engineer ? tacticTicks(rng, 10, 2.5) : 0, scanT: 0, ally: -1, allyX: 0, allyZ: 0, allyLos: 0, fleeX: 0, fleeZ: 0,
    perchX: 0, perchZ: 0, perchOk: 0, perchScanT: 0, hopCd: 0, dead: false,
    hx: new Float32Array(HISTORY_TICKS), hy: new Float32Array(HISTORY_TICKS), hz: new Float32Array(HISTORY_TICKS), hyaw: new Float32Array(HISTORY_TICKS),
  };
}

/** What the AI needs from the game. */
export interface AiHost {
  world: World;
  tick: number;
  rng: Rng;
  players: Map<number, PlayerState>;
  enemies: Enemy[];
  /** every cowboy's camp clock (sim/perch.ts); absent = no perch counters */
  perch?: PerchBook;
  fireBullet(kind: BKind, ox: number, oy: number, oz: number, vx: number, vy: number, vz: number, owner: number): void;
  hurtPlayer(p: PlayerState, dmg: number, fx: number, fz: number, flags: number, kx?: number, kz?: number, bypassShield?: boolean, src?: HitSrc | null): boolean;
  explode(x: number, y: number, z: number, r: number, dmg: number, kind: number, proc?: number, src?: HitSrc | null): void;
  killEnemy(e: Enemy, by: number): void;
  /** an elemental robot's hit landed on a cowboy: its element at L1 (sim/elements.ts procPlayer) */
  elemHitPlayer?(p: PlayerState, elem: number, level?: number): void;
  /** a robot births another (the Swarm Captain's brood) */
  spawnAdhoc?(type: EType, x: number, z: number): Enemy;
  /** the run's mutators (defs.ts Mut): 1 if on */
  mut?(m: number): number;
}

function setState(e: Enemy, st: EState, dur: number) {
  e.st = st; e.stT = 0; e.stDur = st === EState.Windup && SILENT_MUL < 1 ? Math.max(secs(0.35), Math.round(dur * SILENT_MUL)) : dur; e.hitOnce = 0; // SILENT: telegraphs 0.75x, floored
}
/** set by the game each tick from its mutators (a module-level knob keeps setState's signature) */
let SILENT_MUL = 1;
export function setSilent(on: boolean) { SILENT_MUL = on ? MUT_SILENT : 1; }

function pickTarget(e: Enemy, h: AiHost) {
  let best = -1, bestScore = 1e9;
  for (const p of h.players.values()) {
    if (!isTargetable(p) || p.phase !== 0 || isHidden(p)) continue;
    const d = len2(p.x - e.x, p.z - e.z) + Math.abs(p.y - e.y) * 0.5;
    // enemies punish standing still (GAMEPLAY_LOOP §1.5: movement is enforced socially)
    let score = d * (p.stillT > 90 ? 0.6 : 1);
    // ...and camping a perch: arcing fire and snipers go for the camper first (sim/perch.ts)
    const lv = perchLevel(h.perch, p.id);
    if (lv !== PerchLevel.None) score *= LOBBER[e.type] ? 0.35 : e.type === EType.Swarmer && lv === PerchLevel.Flush ? 0.5 : 0.8;
    if (score < bestScore) { bestScore = score; best = p.id; }
  }
  e.target = best;
}

/** Robots whose answer to a perch is to drop something on it (plus the Sniper, who loves a skyline). */
const LOBBER: Record<number, boolean> = { [EType.Bomber]: true, [EType.Engineer]: true, [EType.Disruptor]: true, [EType.Sniper]: true };

/** How far a target stands above this robot, when the perch counters care (0 otherwise). */
const riseOver = (e: Enemy, t: PlayerState) => (PERCH_RULES.counters ? Math.max(0, t.y - e.y) : 0);

function faceToward(e: Enemy, x: number, z: number, rate: number) {
  const want = Math.atan2(-(x - e.x), -(z - e.z));
  const d = wrapAngle(want - e.yaw);
  e.yaw = wrapAngle(e.yaw + clamp(d, -rate * DT, rate * DT));
}

// water-aware pathing: robots don't wade (they hid in streams by accident, half sunk and hard to read). They
// path round ponds and streams, climb out at the nearest bank if knocked in, and only step in to strike a target
// that's right there.
const WATER_PROBES = [1.6, 3.2, 5.0];
const WATER_TURNS = [0.45, 0.9, 1.35, 1.8, 2.3];
const avoidOut = { x: 0, z: 0 };
const wetAhead = (h: AiHost, x: number, z: number, ux: number, uz: number) => {
  for (const d of WATER_PROBES) if (h.world.wetAt(x + ux * d, z + uz * d)) return true;
  return false;
};
/** Along (ux,uz): how far until dry land again past the water ahead (99 if it doesn't end within 12 m). */
const fordLen = (h: AiHost, x: number, z: number, ux: number, uz: number) => {
  let wet = false;
  for (let d = 1; d <= 12; d++) {
    const w = h.world.wetAt(x + ux * d, z + uz * d);
    if (w) wet = true; else if (wet) return d;
  }
  return 99;
};
const FORD_MAX = 9; // a stream this narrow is forded straight across, not walked round
/** The heading (dx,dz) bent round deep water, into avoidOut (same length as the input). */
export function avoidWater(e: Enemy, h: AiHost, dx: number, dz: number) {
  avoidOut.x = dx; avoidOut.z = dz;
  const l = len2(dx, dz);
  if (l < 1e-4) {
    // standing still in water is still standing in water
    if (!h.world.wetAt(e.x, e.z)) return avoidOut;
  }
  const tp = e.target >= 0 ? h.players.get(e.target) : undefined;
  if (tp && len2(tp.x - e.x, tp.z - e.z) < 5) return avoidOut; // close quarters: go and hit them
  const ux = l > 1e-4 ? dx / l : Math.sin(e.yaw) * -1, uz = l > 1e-4 ? dz / l : -Math.cos(e.yaw);
  const sp = l > 1e-4 ? l : 1;
  if (h.world.wetAt(e.x, e.z) && h.world.waterAt(e.x, e.z) > e.y + 0.3) {
    // in the water: keep going if the far bank is close the way it wants to go (mid-ford), else the dry bank
    // nearest that way
    if (l > 1e-4) for (let d = 1; d <= FORD_MAX; d++) if (!h.world.wetAt(e.x + ux * d, e.z + uz * d)) return avoidOut;
    for (const r of [2, 4, 7, 11]) {
      let best = -2, bx = 0, bz = 0;
      for (let k = 0; k < 16; k++) {
        const a = (k / 16) * Math.PI * 2, cx = Math.cos(a), cz = Math.sin(a);
        if (h.world.wetAt(e.x + cx * r, e.z + cz * r)) continue;
        const dot = cx * ux + cz * uz;
        if (dot > best) { best = dot; bx = cx; bz = cz; }
      }
      if (best > -2) { avoidOut.x = bx * sp; avoidOut.z = bz * sp; return avoidOut; }
    }
    return avoidOut;
  }
  if (l < 1e-4 || !wetAhead(h, e.x, e.z, ux, uz)) return avoidOut;
  // a narrow stream: ford it, straight across (a robot crossing is in the open for a second or two, not hiding)
  if (fordLen(h, e.x, e.z, ux, uz) <= FORD_MAX) return avoidOut;
  // bend round it, each robot keeping to its own side (so it doesn't dither as its wanted heading wobbles, and a
  // pack splits round a pond instead of jamming)
  const side = e.id & 1 ? 1 : -1;
  for (const s of [side, -side]) for (const a of WATER_TURNS) {
    const c = Math.cos(a * s), sn = Math.sin(a * s);
    const rx = ux * c - uz * sn, rz = ux * sn + uz * c;
    if (!wetAhead(h, e.x, e.z, rx, rz)) { avoidOut.x = rx * sp; avoidOut.z = rz * sp; return avoidOut; }
  }
  // no dry way round: take the shortest crossing near the wanted heading
  let bestL = 99, bx = ux, bz = uz;
  for (let k = -4; k <= 4; k++) {
    const a = k * 0.35, c = Math.cos(a), sn = Math.sin(a);
    const rx = ux * c - uz * sn, rz = ux * sn + uz * c;
    const fl = fordLen(h, e.x, e.z, rx, rz);
    if (fl < bestL) { bestL = fl; bx = rx; bz = rz; }
  }
  avoidOut.x = bx * sp; avoidOut.z = bz * sp;
  return avoidOut;
}

function steer(e: Enemy, h: AiHost, dx: number, dz: number, speed: number) {
  const aw = avoidWater(e, h, dx, dz);
  dx = aw.x; dz = aw.z;
  if (speed < 1 && (dx !== 0 || dz !== 0)) speed = ENEMIES[e.type].speed * 0.6; // holding still in water: wade out
  const l = len2(dx, dz);
  if (e.slowT > 0) speed *= 0.33; // a Time grenade: a third of the speed (playtest 2)
  speed *= iceSlowMul(e); // ice stacks (sim/elements.ts)
  if (e.limp) speed *= LIMP_MUL; // a leg shot off
  const tx = l > 1e-4 ? (dx / l) * speed : 0, tz = l > 1e-4 ? (dz / l) * speed : 0;
  e.vx += clamp(tx - e.vx, -30 * DT, 30 * DT);
  e.vz += clamp(tz - e.vz, -30 * DT, 30 * DT);
}

const DEG = Math.PI / 180;

// ---------------------------------------------------------------- support robots
/** disruptor.gd _roll_tactic: ~70 % from the support playbook (SHEPHERD / REARGUARD), else any; never the current one. */
export function rollDisruptorTactic(rng: Rng, cur: number): number {
  for (let i = 0; i < 6; i++) {
    const t = rng.chance(0.7) ? (rng.chance(0.5) ? DTactic.Shepherd : DTactic.Rearguard) : rng.int(0, 2);
    if (t !== cur) return t;
  }
  return (cur + 1) % 3;
}
/** engineer.gd _roll_tactic, minus the turret-only FORWARD_WALL. */
export function rollEngineerTactic(rng: Rng, cur: number): number {
  const t = rng.int(0, 2);
  return t === cur ? (t + 1 + rng.int(0, 1)) % 3 : t;
}
const tacticTicks = (rng: Rng, base: number, jitter: number) => secs(Math.max(2, base + rng.range(-jitter, jitter)));

/** SHEPHERD scan: centroid of wounded allies the aura could actually heal. */
function shepherdScan(e: Enemy, h: AiHost) {
  const R2 = DISRUPTOR.shepherdSearchR * DISRUPTOR.shepherdSearchR;
  let cx = 0, cz = 0, n = 0;
  for (const o of h.enemies) {
    if (o === e || o.dead || o.hp >= o.maxHp * DISRUPTOR.auraWoundedFrac) continue;
    const ox = o.x - e.x, oy = o.y - e.y, oz = o.z - e.z;
    if (ox * ox + oy * oy + oz * oz > R2) continue;
    cx += o.x; cz += o.z; n++;
  }
  if (n) { e.allyX = cx / n; e.allyZ = cz / n; e.ally = 1; } else e.ally = -1;
}

function findEnemy(h: AiHost, id: number): Enemy | undefined {
  for (const o of h.enemies) if (o.id === id && !o.dead) return o;
  return undefined;
}
const needsRepair = (o: Enemy) => o.hp < o.maxHp - 0.5;

/** FIELD_MEDIC: keep (or re-pick, on a throttled scan) the most-wounded ally in reach. */
function medicPatient(e: Enemy, h: AiHost): Enemy | undefined {
  let pt = e.ally >= 0 ? findEnemy(h, e.ally) : undefined;
  if (pt && !needsRepair(pt)) { pt = undefined; e.ally = -1; }
  if (--e.scanT <= 0) {
    e.scanT = secs(ENGINEER.scanSecs);
    const R2 = ENGINEER.repairSearchR * ENGINEER.repairSearchR;
    let best: Enemy | undefined, bestFrac = 1;
    for (const o of h.enemies) {
      if (o === e || o.dead || !needsRepair(o)) continue;
      const ox = o.x - e.x, oy = o.y - e.y, oz = o.z - e.z;
      if (ox * ox + oy * oy + oz * oz > R2) continue;
      const f = o.hp / o.maxHp;
      if (f < bestFrac) { bestFrac = f; best = o; }
    }
    if (best) { pt = best; e.ally = best.id; }
    if (pt) {
      const d = ENEMIES[e.type], pd = ENEMIES[pt.type];
      e.allyLos = h.world.lineOfSight(e.x, e.y + d.bodyY, e.z, pt.x, pt.y + pd.bodyY, pt.z) ? 1 : 0;
    }
  }
  return pt;
}

/** Engineer flee-on-hit (engineer.gd _on_hurt_flee): away from the shooter with a sideways juke. */
export function startEngineerFlee(e: Enemy, from: PlayerState | undefined, rng: Rng) {
  if (e.fleeT > 0) return;
  e.fleeT = secs(ENGINEER.fleeSecs);
  let fx: number, fz: number;
  if (from) {
    let ax = e.x - from.x, az = e.z - from.z;
    const al = len2(ax, az) || 1; ax /= al; az /= al;
    const side = rng.chance(0.5) ? 1 : -1;
    fx = ax - az * side; fz = az + ax * side;
  } else { fx = rng.range(-1, 1); fz = rng.range(-1, 1); }
  const l = len2(fx, fz) || 1;
  e.fleeX = fx / l; e.fleeZ = fz / l;
  // getting shot interrupts the repair beam: killing the medic mid-channel is the counterplay
  if (e.st === EState.Windup && e.mode === 1) setState(e, EState.Chase, 0);
}

/** gravity on an airborne robot (the integrator below) */
const ENEMY_G = 24;
/** a Swarmer leap peaks this far over its landing spot */
const HOP_OVER = 3.5;
const HOP_RADII = [6, 9, 12] as const;

/**
 * Swarmer vs a camper: with a clear arc, crouch and leap onto the perch; without one, walk to a
 * spot that has one. Returns true when it took over the tick (the crouch has begun).
 */
function swarmerHop(e: Enemy, h: AiHost, t: PlayerState, dist: number): boolean {
  const rise = riseOver(e, t);
  // the perch's own height counts too: a robot on a hummock doesn't get to leap the tallest spire
  const perchRise = h.perch?.get(t.id)?.rise ?? rise;
  if (!PERCH_RULES.swarmerHop || rise < 2.5 || Math.max(rise, perchRise) > PERCH.hopMaxRise || perchLevel(h.perch, t.id) !== PerchLevel.Flush || !t.grounded) {
    e.perchOk = 0;
    return false;
  }
  if (e.hopCd > 0 || --e.perchScanT > 0) return false;
  e.perchScanT = 10;
  // land just short of the cowboy, on their side of the perch
  const ux = dist > 0.01 ? -(t.x - e.x) / dist : 1, uz = dist > 0.01 ? -(t.z - e.z) / dist : 0;
  const lx = t.x + ux * 0.9, lz = t.z + uz * 0.9;
  const ly = h.world.support(lx, lz, t.y + 0.3);
  const land = ly !== NO_GROUND && Math.abs(ly - t.y) < 0.8;
  const ax = land ? lx : t.x, az = land ? lz : t.z, ay = land ? ly : t.y;
  const clear = (x: number, y: number, z: number) =>
    arcClear(h.world, x, y + 0.6, z, ax, ay + 0.6, az, arcFlight(y, ay, ENEMY_G, 0.5, HOP_OVER), ENEMY_G);
  if (dist > 2.5 && dist < PERCH.hopMaxDist && clear(e.x, e.y, e.z)) {
    e.aimX = ax; e.aimY = ay; e.aimZ = az; e.mode = 1; e.perchOk = 0;
    setState(e, EState.Windup, secs(PERCH.hopCrouchSecs));
    return true;
  }
  const s = ringSpot(h.world, t.x, t.z, HOP_RADII, e.y, Math.atan2(e.z - t.z, e.x - t.x), clear);
  if (s) { e.perchX = s.x; e.perchZ = s.z; e.perchOk = 1; } else e.perchOk = 0;
  return false;
}

/**
 * Shooter / Sniper vs a high target: a spot on the floor that can see them. Off the perch lip's
 * shadow, and (once they've camped a while) round the perch on this robot's own bearing, so
 * the squad spreads into a crossfire.
 */
function sightSpot(e: Enemy, h: AiHost, t: PlayerState, lo: number, hi: number) {
  const rise = riseOver(e, t);
  if (rise < PERCH.minRise) { e.perchOk = 0; return; }
  if (--e.perchScanT > 0) return;
  e.perchScanT = 15;
  const eyeY = ENEMIES[e.type].headY;
  const sees = (x: number, y: number, z: number) => h.world.lineOfSight(x, y + eyeY, z, t.x, t.y + 1.2, t.z);
  const camping = perchLevel(h.perch, t.id) !== PerchLevel.None;
  if (!camping && sees(e.x, e.y, e.z)) { e.perchOk = 0; return; }
  // stepping back flattens the angle over the perch's lip
  const r = clamp(rise * 1.3, lo, hi);
  const radii = [r, Math.min(r * 1.45, hi + 12), hi + 12];
  const home = Math.atan2(e.z - t.z, e.x - t.x);
  // golden-angle bearings by id: neighbours in a squad end up on different sides
  const a0 = camping && e.type === EType.Shooter ? (e.id * 2.39996) % (Math.PI * 2) : home;
  const s = ringSpot(h.world, t.x, t.z, radii, e.y, a0, sees);
  if (s) { e.perchX = s.x; e.perchZ = s.z; e.perchOk = 1; } else e.perchOk = 0;
}

/** Lob from a robot's hands to a ground point. */
function lob(h: AiHost, e: Enemy, kind: BKind, oy: number, flight: number) {
  const sy = e.y + oy;
  const v = lobVelocity(e.x, sy, e.z, e.aimX, e.aimY, e.aimZ, flight, BULLETS[kind].gravity);
  h.fireBullet(kind, e.x, sy, e.z, v.vx, v.vy, v.vz, e.id);
}

/** The per-tick status bookkeeping every enemy shares (timers, prime expiry, shield regen, burn).
 *  Returns false if the burn killed it. Boss units call this from their own brains (sim/bosses). */
/** the Stalker (Godot stalker.gd): cloaked until it strikes or is hit (STALK_REVEAL), it stalks in on one of three tactics,
 *  bites for STALK_DMG at STALK_REACH, then backs off and fades again. Tactics re-roll every ~10 s while engaged:
 *  0 straight pounce; 1 circle: orbit at reach and only strike from the rear arc (or when patience runs out);
 *  2 hit-and-fade: come in off to one side, strike, retreat farther and longer, flip sides. */
function stepStalker(e: Enemy, h: AiHost, t: PlayerState | undefined, dist: number, dx: number, dz: number) {
  const def = ENEMIES[e.type];
  if (e.retreatT > 0) {
    e.retreatT--;
    if (t) { const ax = e.x - t.x, az = e.z - t.z, l = len2(ax, az) || 1; steer(e, h, ax / l, az / l, STALK_RETREAT * (e.tactic === 2 ? STALK_FADE_MUL : 1)); }
    return;
  }
  if (!t) return;
  // the tactic clock (only while engaged); a different one each time
  if (--e.tacticT <= 0) {
    let next = e.tactic;
    while (next === e.tactic) next = h.rng.int(0, 2);
    e.tactic = next; e.tacticT = secs(Math.max(2, STALK_TACTIC_SECS + h.rng.range(-STALK_TACTIC_JITTER, STALK_TACTIC_JITTER)));
    if (next === 1) { e.patienceT = secs(STALK_PATIENCE); e.circleSign = h.rng.chance(0.5) ? 1 : -1; }
  }
  faceToward(e, t.x, t.z, 8);
  const nx = dx / (dist || 1), nz = dz / (dist || 1);
  const reach = STALK_REACH + def.radius;
  const canBite = dist <= reach && e.atkCd === 0 && Math.abs(t.y - e.y) < 2;
  if (e.tactic === 1) {
    if (e.patienceT > 0) e.patienceT--;
    // behind the cowboy: the vector from them to the Stalker against their facing
    const fx = -Math.sin(t.yaw), fz = -Math.cos(t.yaw);
    const behind = (-nx) * fx + (-nz) * fz <= STALK_REAR_COS;
    if (canBite && (behind || e.patienceT === 0)) { stalkerBite(e, h, t, nx, nz); return; }
    const outer = reach + STALK_ORBIT;
    const tx = nz * e.circleSign, tz = -nx * e.circleSign;
    const radial = dist > outer || behind || e.patienceT === 0 ? 1 : dist < reach ? -1 : 0; // an opening (or patience spent): close in
    steer(e, h, tx * (behind ? 0.3 : 1) + nx * radial, tz * (behind ? 0.3 : 1) + nz * radial, STALK_SPEED);
    return;
  }
  if (canBite) { stalkerBite(e, h, t, nx, nz); return; }
  let sx = nx, sz = nz;
  if (e.tactic === 2 && dist > reach * 1.5) { sx = nx + nz * e.fadeSign * 0.6; sz = nz - nx * e.fadeSign * 0.6; const l = len2(sx, sz) || 1; sx /= l; sz /= l; }
  steer(e, h, sx, sz, STALK_SPEED);
}
function stalkerBite(e: Enemy, h: AiHost, t: PlayerState, nx: number, nz: number) {
  e.revealT = secs(STALK_REVEAL);
  e.atkCd = secs(STALK_CD);
  if (h.hurtPlayer(t, STALK_DMG, e.x, e.z, 4, nx * STALK_KNOCK, nz * STALK_KNOCK, false, e) && e.armorEl !== Elem.None) h.elemHitPlayer?.(t, e.armorEl, e.elLevel);
  t.hitStunT = Math.max(t.hitStunT, Math.round(STALK_STAGGER / 60 * 60 * 0.25)); // the stagger force lands as a beat of hit stun
  setState(e, EState.Attack, secs(0.25));
  e.retreatT = secs(1.5 * (e.tactic === 2 ? STALK_FADE_MUL : 1));
  if (e.tactic === 2) e.fadeSign = -e.fadeSign;
}

/** the Support Helicopter (support_helicopter.gd): never attacks. It wards the biggest living robot within reach with a
 *  60 shield that regrows at 35/s, orbits it at HELI_ORBIT, hoses the ground wet every HELI_SPRAY_SECS (robots wet, burning
 *  robots doused, cowboys in the spray lose heat), and heals everything under it 4/s. Shoot it first. */
function stepHelicopter(e: Enemy, h: AiHost, t: PlayerState | undefined, def: EnemyDef) {
  // the ward: the biggest living robot within HELI_WARD_RANGE that isn't a flier (re-picked when it dies)
  let w = e.ward >= 0 ? findEnemy(h, e.ward) : undefined;
  if (!w || w.dead) {
    e.ward = -1; let best = 0;
    for (const o of h.enemies) { if (o.dead || o === e || isFlier(o.type) || o.type === EType.Turret || isBossType(o.type) || len2(o.x - e.x, o.z - e.z) > HELI_WARD_RANGE) continue; if (o.maxHp > best) { best = o.maxHp; w = o; } }
    if (w) { e.ward = w.id; if (w.maxShield < HELI_SHIELD) w.maxShield = HELI_SHIELD; w.shield = Math.max(w.shield, HELI_SHIELD * 0.5); }
  }
  if (w && !w.dead) {
    // the projected shield regrows fast while the helicopter lives
    if (w.shield < HELI_SHIELD) w.shield = Math.min(HELI_SHIELD, w.shield + HELI_SHIELD_REGEN * DT);
    // orbit it
    const a = (h.tick * 0.012) + e.id;
    const tx = w.x + Math.cos(a) * HELI_ORBIT, tz = w.z + Math.sin(a) * HELI_ORBIT;
    steer(e, h, tx - e.x, tz - e.z, def.speed);
    faceToward(e, w.x, w.z, 3);
  } else if (t) { steer(e, h, t.x - e.x, t.z - e.z, def.speed * 0.6); faceToward(e, t.x, t.z, 3); }
  if (e.resting || e.restT > 0) return;
  // the spray and the heal aura
  if (e.spraying > 0) {
    e.spraying--;
    for (const o of h.enemies) if (!o.dead && o !== e && len2(o.x - e.x, o.z - e.z) < HELI_SPRAY_R) { o.wetT = Math.max(o.wetT, secs(4)); if (o.burnT > 0) o.burnT = 0; }
    for (const p of h.players.values()) if (p.phase === 0 && len2(p.x - e.x, p.z - e.z) < HELI_SPRAY_R) { p.heat = Math.max(0, p.heat - HELI_SPRAY_COOL * DT); p.wetT = Math.max(p.wetT, secs(4)); if (p.onFire) p.onFire = 0; }
  } else if (--e.sprayT <= 0) { e.sprayT = secs(HELI_SPRAY_SECS); e.spraying = secs(HELI_SPRAY_DUR); e.mode = 1; setState(e, EState.Attack, secs(HELI_SPRAY_DUR)); }
  if (h.tick % 15 === 0) for (const o of h.enemies) if (!o.dead && o !== e && !isBossType(o.type) && o.hp < o.maxHp && len2(o.x - e.x, o.z - e.z) < HELI_HEAL_R) o.hp = Math.min(o.maxHp, o.hp + HELI_HEAL * 0.25);
}

export function stepStatus(e: Enemy, h: AiHost): boolean {
  if (e.atkCd > 0) e.atkCd -= h.mut?.(7) ? 1.25 : 1; // FRENZIED: cooldowns run 0.8x
  if (e.atkCd < 0) e.atkCd = 0;
  if (e.fleeT > 0 && h.mut?.(1)) e.fleeT = 0; // ZEALOUS: nothing flees
  if (e.primeStunCd > 0) e.primeStunCd--;
  if (e.hopCd > 0) e.hopCd--;
  if (e.vulnT > 0) e.vulnT--;
  if (e.lensT > 0) e.lensT--;
  if (e.revealT > 0) e.revealT--; // the Stalker re-cloaks
  if (e.shedCd > 0) e.shedCd--;
  if (e.type === EType.Turret && --e.lifeT <= 0) { h.killEnemy(e, -1); return false; } // a turret runs down
  // Aegis: broken, it refills whole after AEGIS_DELAY_SECS unhit (a magnetic lock holds it off)
  if (e.aegisT > 0 && --e.aegisT === 0) { if (e.magT > 0) e.aegisT = 30; else e.shield = e.maxShield; }
  if (e.stripT > 0 && --e.stripT === 0) e.stripN = 1; // the acid's tier count goes with the strip (sim/elements.ts)
  if (e.slowT > 0) e.slowT--;
  if (e.fleeT > 0) e.fleeT--;
  if (e.primedBy >= 0 && e.primeT > 0) { e.primeT--; if (e.primeT === 0) e.primedBy = -1; }
  if (e.maxShield > 0 && e.shield < e.maxShield) {
    if (e.shieldRegenT > 0) e.shieldRegenT--;
    else if (e.aegisT === 0) e.shield = Math.min(e.maxShield, e.shield + e.maxShield * 0.25 * DT); // Aegis: nothing trickles back, it comes back whole
  }
  if (e.burnT > 0) {
    e.burnT--;
    e.hp -= (e.burnDps || 6) * DT; // the fire's rate by its level (sim/elements.ts)
    if (e.burnT === 0) e.burnDps = 6;
    if (e.hp <= 0) { h.killEnemy(e, e.burnOwner); return false; }
  }
  tickEnemyElems(e, h as never);
  return true;
}

/** Run one robot for one tick. */
export function stepEnemy(e: Enemy, h: AiHost) {
  const def = ENEMIES[e.type];
  e.stT++;
  if (!stepStatus(e, h)) return;

  if (--e.retargetT <= 0) { pickTarget(e, h); e.retargetT = 30; }
  const tp = e.target >= 0 ? h.players.get(e.target) : undefined;
  // a cloaked Phantom drops out of every robot's sights: it looks for someone else on the next retarget
  if (tp && (!isTargetable(tp) || tp.phase !== 0 || isHidden(tp))) { e.target = -1; e.retargetT = Math.min(e.retargetT, 6); }
  const t = e.target >= 0 ? tp : undefined;
  const dx = t ? t.x - e.x : 0, dz = t ? t.z - e.z : 0;
  const dist = len2(dx, dz);

  let wantVx = 0, wantVz = 0, hold = false;
  if (e.staggerT > 0) e.staggerT--;
  if (e.graceT > 0 && --e.graceT === 0) e.graceBy = -1;
  if (e.flinchT > 0) {
    // the micro-flinch: a round landed, the robot stops dead for a couple of ticks (Godot micro_stun)
    e.flinchT--;
    hold = true;
  } else if (e.stunT > 0) {
    e.stunT--;
    if (e.st !== EState.Stun) setState(e, EState.Stun, e.stunT);
    hold = true;
    if (e.stunT === 0) setState(e, EState.Chase, 0);
  } else if (e.st === EState.Spawn) {
    hold = true;
    if (e.stT >= e.stDur) setState(e, EState.Chase, 0);
  } else if (e.routT > 0) {
    // morale (ROSTER.md): routing chaff runs from the nearest cowboy and sinks away at the end of it, loot-free
    if (--e.routT === 0) { h.killEnemy(e, -1); return; }
    let nx = 0, nz = 0, best = 1e9;
    for (const p of h.players.values()) { if (p.phase !== 0) continue; const d = len2(p.x - e.x, p.z - e.z); if (d < best) { best = d; nx = e.x - p.x; nz = e.z - p.z; } }
    const l = len2(nx, nz) || 1;
    if (best < 1e9) { steer(e, h, nx / l, nz / l, def.speed * 1.15); faceToward(e, e.x + nx, e.z + nz, 8); }
  } else {
    // the Swarm Captain births a brood every CAPTAIN_SPAWN_SECS (swarm_captain.gd)
    if (e.big && e.type === EType.Swarmer && h.spawnAdhoc && ++e.broodT >= secs(CAPTAIN_SPAWN_SECS)) {
      e.broodT = 0;
      for (let i = 0; i < CAPTAIN_BATCH; i++) { const a = (i / CAPTAIN_BATCH) * Math.PI * 2 + e.id; const k = h.spawnAdhoc(EType.Swarmer, e.x + Math.cos(a) * CAPTAIN_RING, e.z + Math.sin(a) * CAPTAIN_RING); k.broodOf = e.id; k.wave = e.wave; }
    }
    switch (e.type) {
      case EType.Stalker: {
        stepStalker(e, h, t, dist, dx, dz);
        break;
      }
      case EType.Helicopter: {
        stepHelicopter(e, h, t, def);
        break;
      }
      case EType.Turret: {
        // engineer.gd turret: planted, fires a pellet at whoever it sees within reach, every TURRET_FIRE_SECS
        hold = true;
        if (t && dist < TURRET_RANGE && e.atkCd === 0) {
          faceToward(e, t.x, t.z, 12);
          const sy = e.y + def.headY;
          if (h.world.lineOfSight(e.x, sy, e.z, t.x, t.y + 1.0, t.z)) {
            const ax = t.x - e.x, ay = t.y + 1.0 - sy, az = t.z - e.z, l = Math.sqrt(ax * ax + ay * ay + az * az) || 1, sp = BULLETS[BKind.Pellet].speed;
            h.fireBullet(BKind.Pellet, e.x, sy, e.z, (ax / l) * sp, (ay / l) * sp, (az / l) * sp, e.id);
            e.atkCd = secs(TURRET_FIRE_SECS);
          } else e.atkCd = 15;
        }
        break;
      }
      case EType.Swarmer: {
        if (e.st === EState.Chase) {
          if (t) {
            if (swarmerHop(e, h, t, dist)) break;
            const weave = Math.sin((h.tick + e.id * 37) * 0.09) * 0.6;
            let nx = dx / (dist || 1), nz = dz / (dist || 1);
            if (e.perchOk) {
              // walking to a launch spot with a clear arc onto the perch
              const lx = e.perchX - e.x, lz = e.perchZ - e.z, ll = len2(lx, lz) || 1;
              nx = lx / ll; nz = lz / ll;
            }
            steer(e, h, nx - nz * weave, nz + nx * weave, def.speed);
            faceToward(e, t.x, t.z, 8);
            if (dist < 4.4 && e.atkCd === 0 && Math.abs(t.y - e.y) < 2.5) {
              e.mode = 0;
              setState(e, EState.Windup, secs(0.45));
            }
          } else hold = true;
        } else if (e.st === EState.Windup && e.mode === 1) {
          // the toaster crouch: springs load, slots glow, then POP
          hold = true;
          if (t) faceToward(e, t.x, t.z, 10);
          if (e.stT >= e.stDur) {
            const fl = arcFlight(e.y, e.aimY, ENEMY_G, 0.5, HOP_OVER);
            const v = lobVelocity(e.x, e.y, e.z, e.aimX, e.aimY, e.aimZ, fl, ENEMY_G);
            e.vx = v.vx; e.vz = v.vz; e.vy = v.vy; e.airborne = 1; e.y += 0.05;
            setState(e, EState.Attack, secs(fl + 1.5));
            e.hopCd = secs(PERCH.hopCd);
          }
        } else if (e.st === EState.Attack && e.mode === 1) {
          // in the air: ballistic until it lands (on the perch, or back on the floor if shot down)
          if (!e.airborne || e.stT >= e.stDur) { e.mode = 0; setState(e, EState.Recover, secs(0.35)); e.atkCd = Math.min(e.atkCd, secs(0.3)); }
        } else if (e.st === EState.Windup) {
          hold = true;
          if (t && e.stT < e.stDur - 6) { faceToward(e, t.x, t.z, 10); e.aimX = t.x; e.aimZ = t.z; }
          if (e.stT >= e.stDur) setState(e, EState.Attack, secs(0.32));
        } else if (e.st === EState.Attack) {
          const ax = e.aimX - e.x, az = e.aimZ - e.z;
          const l = len2(ax, az) || 1;
          const fx = -Math.sin(e.yaw), fz = -Math.cos(e.yaw);
          e.vx = fx * 13; e.vz = fz * 13; void l;
          if (!e.hitOnce) {
            for (const p of h.players.values()) {
              if (!isTargetable(p) || p.phase !== 0) continue;
              if (len2(p.x - e.x, p.z - e.z) < def.radius + 0.7 && Math.abs(p.y - e.y) < 1.6) {
                if (h.hurtPlayer(p, 12, e.x, e.z, 4, fx * 7, fz * 7, false, e) && e.armorEl !== Elem.None) h.elemHitPlayer?.(p, e.armorEl, e.elLevel); e.hitOnce = 1;
              }
            }
          }
          if (e.stT >= e.stDur) { setState(e, EState.Recover, secs(0.6)); e.atkCd = secs(1.1); }
        } else if (e.st === EState.Recover) {
          hold = true;
          if (e.stT >= e.stDur) setState(e, EState.Chase, 0);
        }
        break;
      }
      case EType.Shooter:
      case EType.Bomber:
      case EType.Sniper:
      case EType.FlyingShooter:
      case EType.FlyingSniper: {
        const sniper = e.type === EType.Sniper || e.type === EType.FlyingSniper, bomber = e.type === EType.Bomber, flier = e.hoverAlt > 0;
        if (flier && (e.resting || e.restT > 0)) { hold = true; if (t) faceToward(e, t.x, t.z, 4); break; } // jets off on the ground: no shots
        if (!bomber && !flier && e.gunHp <= 0) {
          // disarmed (weapon_sever.gd): the Shooter shambles in to claw; the Sniper keeps its distance and never fires again
          if (e.st !== EState.Chase && e.st !== EState.Attack) setState(e, EState.Chase, 0);
          if (!t) { hold = true; break; }
          if (sniper) { steer(e, h, -dx, -dz, def.speed); faceToward(e, t.x, t.z, 6); break; }
          steer(e, h, dx, dz, def.speed * 1.3); faceToward(e, t.x, t.z, 8);
          if (dist < def.radius + 1.2 && e.atkCd === 0 && Math.abs(t.y - e.y) < 2) {
            const nx = dx / (dist || 1), nz = dz / (dist || 1);
            if (h.hurtPlayer(t, 8, e.x, e.z, 4, nx * 5, nz * 5, false, e) && e.armorEl !== Elem.None) h.elemHitPlayer?.(t, e.armorEl, e.elLevel);
            e.atkCd = secs(1.1); setState(e, EState.Attack, secs(0.3));
          }
          break;
        }
        const lo = sniper ? 28 : bomber ? 11 : 13, hi = sniper ? 46 : bomber ? 20 : 22;
        if (e.st === EState.Chase) {
          if (t) {
            faceToward(e, t.x, t.z, 4);
            const nx = dx / (dist || 1), nz = dz / (dist || 1);
            let radial = dist > hi ? 1 : dist < lo ? -1 : 0;
            if (e.fleeT > 0) radial = -1.4;
            if ((h.tick + e.id * 13) % 240 === 0) e.strafe = -e.strafe;
            // a camper is a Bomber's favourite target: arcing fire needs no line of sight
            const camper = bomber && riseOver(e, t) >= PERCH.minRise && perchLevel(h.perch, t.id) !== PerchLevel.None;
            if (!bomber && !flier) sightSpot(e, h, t, lo, hi);
            const sx = e.perchX - e.x, sz = e.perchZ - e.z, sl = len2(sx, sz);
            if (!flier && e.perchOk && e.fleeT === 0 && sl > 1.5) steer(e, h, sx, sz, def.speed * 1.15);
            else steer(e, h, nx * radial - nz * e.strafe * 0.7, nz * radial + nx * e.strafe * 0.7, def.speed);
            if (e.atkCd === 0 && dist < hi + 14 && e.fleeT === 0) {
              const eyeY = e.y + def.headY;
              if (camper || h.world.lineOfSight(e.x, eyeY, e.z, t.x, t.y + 1.2, t.z)) {
                setState(e, EState.Windup, sniper ? secs(1.4) : bomber ? secs(0.9) : secs(0.7));
              } else e.atkCd = 20;
            }
          } else hold = true;
        } else if (e.st === EState.Windup) {
          hold = true;
          // the sniper's laser tracks you until it locks, a quarter second before the shot
          const lock = sniper ? secs(0.25) : 8;
          if (t && e.stT < e.stDur - lock) {
            faceToward(e, t.x, t.z, sniper ? 3 : 6);
            e.aimX = t.x; e.aimY = t.y + (bomber ? 0 : 1.1); e.aimZ = t.z;
          }
          if (e.stT >= e.stDur) {
            const sx = e.x - Math.sin(e.yaw) * def.radius, sz = e.z - Math.cos(e.yaw) * def.radius;
            const sy = e.y + (sniper ? def.headY : bomber ? def.headY + 0.3 : def.bodyY + 0.2);
            if (bomber) {
              const bg = BULLETS[BKind.Bomb].gravity;
              const fl = e.aimY - sy > PERCH.minRise - 2 && PERCH_RULES.counters ? arcFlight(sy, e.aimY, bg, 1.25) : 1.25;
              const v = lobVelocity(sx, sy, sz, e.aimX, e.aimY, e.aimZ, fl, bg);
              h.fireBullet(BKind.Bomb, sx, sy, sz, v.vx, v.vy, v.vz, e.id);
            } else {
              const ax = e.aimX - sx, ay = e.aimY - sy, az = e.aimZ - sz;
              const l = Math.sqrt(ax * ax + ay * ay + az * az) || 1;
              const kind = sniper ? BKind.Slug : BKind.Pellet;
              const sp = BULLETS[kind].speed;
              if (sniper) h.fireBullet(kind, sx, sy, sz, (ax / l) * sp, (ay / l) * sp, (az / l) * sp, e.id);
              else {
                // a fan of five: the bullet ballet
                const base = Math.atan2(ax, az);
                const flat = len2(ax, az) / l;
                for (let i = -2; i <= 2; i++) {
                  const a = base + i * 9 * DEG;
                  h.fireBullet(kind, sx, sy, sz, Math.sin(a) * sp * flat, (ay / l) * sp, Math.cos(a) * sp * flat, e.id);
                }
              }
            }
            setState(e, EState.Recover, secs(0.5));
            e.atkCd = secs(sniper ? h.rng.range(3.4, 4.6) : bomber ? h.rng.range(2.8, 3.8) : h.rng.range(2.0, 3.0));
          }
        } else if (e.st === EState.Recover) {
          hold = true;
          if (e.stT >= e.stDur) setState(e, EState.Chase, 0);
        }
        break;
      }
      case EType.Brute:
      case EType.MetalBrute: {
        const big = e.type === EType.MetalBrute;
        if (e.st === EState.Chase) {
          if (t) {
            const rise = riseOver(e, t);
            // up on a perch, slams and charges can't reach: tear up the ground and throw it
            const throwUp = rise >= PERCH.minRise && (big || (PERCH_RULES.bruteBoulders && perchLevel(h.perch, t.id) !== PerchLevel.None));
            // ...from far enough back that the rock clears the lip instead of the underside
            const standOff = throwUp ? Math.max(8, rise * 0.9) : 0;
            if (dist < standOff - 1) steer(e, h, -dx, -dz, def.speed * 0.8);
            else if (dist < standOff + 2) steer(e, h, -dz * e.strafe, dx * e.strafe, def.speed * 0.5);
            else steer(e, h, dx, dz, def.speed);
            faceToward(e, t.x, t.z, 3);
            if (e.atkCd === 0 && throwUp && dist < (big ? 40 : 26) && dist >= standOff - 2) {
              const sy = e.y + def.headY, bg = BULLETS[BKind.Boulder].gravity;
              if (arcClear(h.world, e.x, sy, e.z, t.x, t.y + 0.5, t.z, arcFlight(sy, t.y, bg, 1.5), bg)) { e.mode = 2; setState(e, EState.Windup, secs(1.1)); }
              else e.atkCd = 20;
            }
            else if (e.atkCd === 0 && rise < PERCH.minRise) {
              if (dist < (big ? 6 : 3.4)) { e.mode = 1; setState(e, EState.Windup, secs(big ? 0.9 : 0.7)); }
              else if (dist < (big ? 18 : 11)) { e.mode = 0; setState(e, EState.Windup, secs(big ? 1.0 : 0.85)); }
              else if (big && dist < 40) { e.mode = 2; setState(e, EState.Windup, secs(1.1)); }
            }
          } else hold = true;
        } else if (e.st === EState.Windup) {
          hold = true;
          if (t && e.stT < e.stDur - 10) { faceToward(e, t.x, t.z, 4); e.aimX = t.x; e.aimY = t.y; e.aimZ = t.z; }
          if (e.stT >= e.stDur) {
            if (e.mode === 1) {
              h.explode(e.x - Math.sin(e.yaw) * 1.5, e.y, e.z - Math.cos(e.yaw) * 1.5, big ? 6 : 3.8, big ? 26 : 18, 1, 0, e);
              setState(e, EState.Recover, secs(big ? 1.1 : 0.9));
              e.atkCd = secs(1.6);
            } else if (e.mode === 2) {
              // a volley from the Metal Brute, one rock from a plain Brute; tighter onto a perch
              const sy = e.y + def.headY, bg = BULLETS[BKind.Boulder].gravity;
              const high = PERCH_RULES.counters && e.aimY - sy > PERCH.minRise - 2;
              const fl = high ? arcFlight(sy, e.aimY, bg, 1.5) : 1.5, spread = high ? 1.6 : 4;
              for (let i = big ? 0 : 1; i < (big ? 3 : 2); i++) {
                const ox = (i - 1) * spread, oz = h.rng.range(-0.75, 0.75) * spread;
                const v = lobVelocity(e.x, sy, e.z, e.aimX + ox, e.aimY, e.aimZ + oz, fl, bg);
                h.fireBullet(BKind.Boulder, e.x, sy, e.z, v.vx, v.vy, v.vz, e.id);
              }
              setState(e, EState.Recover, secs(1.0));
              e.atkCd = secs(3.5);
            } else setState(e, EState.Attack, secs(big ? 1.0 : 0.8));
          }
        } else if (e.st === EState.Attack) {
          const fx = -Math.sin(e.yaw), fz = -Math.cos(e.yaw);
          e.vx = fx * (big ? 15 : 14); e.vz = fz * (big ? 15 : 14);
          if (!e.hitOnce) {
            for (const p of h.players.values()) {
              if (!isTargetable(p) || p.phase !== 0) continue;
              if (len2(p.x - e.x, p.z - e.z) < def.radius + 0.8 && Math.abs(p.y - e.y) < 2.5) {
                if (h.hurtPlayer(p, big ? 32 : 24, e.x, e.z, 4, fx * 14, fz * 14, false, e) && e.armorEl !== Elem.None) h.elemHitPlayer?.(p, e.armorEl, e.elLevel); e.hitOnce = 1;
              }
            }
          }
          if (e.stT >= e.stDur) { setState(e, EState.Recover, secs(1.0)); e.atkCd = secs(2.4); }
        } else if (e.st === EState.Recover) {
          hold = true;
          if (e.stT >= e.stDur) setState(e, EState.Chase, 0);
        }
        break;
      }
      case EType.Disruptor: {
        // disruptor.gd: no direct damage. A 6 m aura heals wounded allies and refills shields;
        // ice / magnetic grenades zone the posse. Tactic shapes range and throw cadence.
        const D = DISRUPTOR, tm = e.maxHp / def.hp;
        const ar2 = D.auraR * D.auraR;
        for (const o of h.enemies) {
          if (o === e || o.dead) continue;
          const ox = o.x - e.x, oy = o.y - e.y, oz = o.z - e.z;
          if (ox * ox + oy * oy + oz * oz > ar2) continue;
          if (o.hp < o.maxHp * D.auraWoundedFrac) o.hp = Math.min(o.maxHp, o.hp + D.auraHeal * tm * DT);
          if (o.shield < o.maxShield) o.shield = Math.min(o.maxShield, o.shield + D.auraShield * tm * DT);
        }
        if (t) {
          if (e.tactic === DTactic.Shepherd && --e.scanT <= 0) { e.scanT = 15; shepherdScan(e, h); }
          if (--e.tacticT <= 0) {
            e.tactic = rollDisruptorTactic(h.rng, e.tactic);
            e.tacticT = tacticTicks(h.rng, D.tacticSecs, D.tacticJitter);
            e.scanT = 0; e.ally = -1;
          }
        }
        const keep = D.range + (e.tactic === DTactic.Rearguard ? D.rearguardExtra : 0);
        if (e.st === EState.Chase) {
          if (!t) { hold = true; break; }
          faceToward(e, t.x, t.z, 4);
          const nx = dx / (dist || 1), nz = dz / (dist || 1);
          let mx = 0, mz = 0, sp = def.speed;
          if (e.tactic === DTactic.Shepherd && e.ally >= 0) {
            // herd support: slide the aura over the wounded, but never close on the cowboy
            let hx = e.allyX - e.x, hz = e.allyZ - e.z;
            const hl = len2(hx, hz);
            if (hl > 0.01) { hx /= hl; hz /= hl; } else { hx = 0; hz = 0; }
            const dot = hx * nx + hz * nz;
            if (dist <= keep && dot > 0) {
              hx -= nx * dot; hz -= nz * dot;
              const l = len2(hx, hz);
              if (l * l > 0.001) { hx /= l; hz /= l; } else { hx = 0; hz = 0; }
            }
            mx = hx; mz = hz;
          } else if (e.tactic !== DTactic.Shepherd) {
            if (dist < keep) { mx = -nx; mz = -nz; } // ZONER / REARGUARD kite hard
          } else if (dist < keep - 3) { mx = -nx; mz = -nz; }
          else if (dist > keep + 5) { mx = nx; mz = nz; sp *= 0.5; }
          steer(e, h, mx, mz, sp);
          // Godot throws the instant the cooldown is up; the port adds the screen-face telegraph
          if (e.atkCd === 0 && dist < keep + 10) setState(e, EState.Windup, secs(0.6));
        } else if (e.st === EState.Windup) {
          hold = true;
          if (t && e.stT < e.stDur - 8) { faceToward(e, t.x, t.z, 6); e.aimX = t.x; e.aimY = t.y; e.aimZ = t.z; }
          if (e.stT >= e.stDur) {
            // 50/50 ice (slow stacks) or magnetic (shield-regen lock); the proc is the threat, not the chip
            const kind = h.rng.chance(0.5) ? BKind.IceNade : BKind.MagNade;
            let fl = clamp(len2(e.aimX - e.x, e.aimZ - e.z) / 14, 0.5, 1.6);
            if (PERCH_RULES.counters && e.aimY - e.y > PERCH.minRise) fl = arcFlight(e.y + def.bodyY, e.aimY, BULLETS[kind].gravity, fl);
            lob(h, e, kind, def.bodyY, fl);
            const mul = e.tactic === DTactic.Zoner ? D.zonerMul : e.tactic === DTactic.Rearguard ? D.rearguardMul : 1;
            e.atkCd = secs(D.grenadeCd * mul);
            setState(e, EState.Recover, secs(0.5));
          }
        } else if (e.st === EState.Recover) {
          hold = true;
          if (e.stT >= e.stDur) setState(e, EState.Chase, 0);
        } else setState(e, EState.Chase, 0);
        break;
      }
      case EType.Engineer: {
        // engineer.gd: no direct damage. Gas canisters zone the posse; the FIELD_MEDIC tactic
        // channels a repair beam on the most-wounded ally; getting shot sends it running.
        const E = ENGINEER, tm = e.maxHp / def.hp;
        // the turret (engineer.gd): one planted at a time, every TURRET_DEPLOY_SECS, where it stands (it then hugs it)
        if (t && h.spawnAdhoc && e.fleeT === 0 && --e.deployT <= 0 && !h.enemies.some((o) => !o.dead && o.type === EType.Turret && o.broodOf === e.id)) {
          e.deployT = secs(TURRET_DEPLOY_SECS);
          const k = h.spawnAdhoc(EType.Turret, e.x - Math.sin(e.yaw) * 1.2, e.z - Math.cos(e.yaw) * 1.2); k.broodOf = e.id; k.wave = e.wave;
        }
        if (t && --e.tacticT <= 0) {
          e.tactic = rollEngineerTactic(h.rng, e.tactic);
          e.tacticT = tacticTicks(h.rng, E.tacticSecs, E.tacticJitter);
          e.scanT = 0; e.ally = -1;
        }
        const channel = e.st === EState.Windup && e.mode === 1;
        if (e.st === EState.Chase || channel) {
          if (e.fleeT > 0) {
            if (channel) setState(e, EState.Chase, 0);
            steer(e, h, e.fleeX, e.fleeZ, def.speed * 1.5);
            faceToward(e, e.x + e.fleeX, e.z + e.fleeZ, 6);
            break;
          }
          if (!t) { if (channel) setState(e, EState.Chase, 0); hold = true; break; }
          if (e.tactic === ETactic.FieldMedic) {
            const pt = medicPatient(e, h);
            if (pt) {
              const px = pt.x - e.x, pz = pt.z - e.z;
              faceToward(e, pt.x, pt.z, 6);
              if (len2(px, pz) > E.repairRange || !e.allyLos) {
                if (channel) setState(e, EState.Chase, 0);
                steer(e, h, px, pz, def.speed);
              } else {
                hold = true;
                pt.hp = Math.min(pt.maxHp, pt.hp + E.repairPerSec * tm * DT);
                e.aimX = pt.x; e.aimY = pt.y + ENEMIES[pt.type].bodyY; e.aimZ = pt.z;
                // the copy-light sweep loops for as long as the beam holds
                if (!channel || e.stT >= e.stDur) { e.mode = 1; setState(e, EState.Windup, secs(0.5)); }
              }
              break;
            }
            if (channel) setState(e, EState.Chase, 0);
          }
          const keep = e.tactic === ETactic.TurtleRepair ? E.turtleRange : E.range;
          faceToward(e, t.x, t.z, 4);
          if (dist < keep - 4) steer(e, h, -dx, -dz, def.speed);
          else if (dist > keep + 4) steer(e, h, dx, dz, def.speed * 0.5);
          else steer(e, h, 0, 0, 0);
          if (e.atkCd === 0 && dist < E.range + 8) { e.mode = 0; setState(e, EState.Windup, secs(0.6)); }
        } else if (e.st === EState.Windup) {
          hold = true;
          if (t && e.stT < e.stDur - 8) { faceToward(e, t.x, t.z, 6); e.aimX = t.x; e.aimY = t.y; e.aimZ = t.z; }
          if (e.stT >= e.stDur) {
            const high = PERCH_RULES.counters && e.aimY - e.y > PERCH.minRise;
            lob(h, e, BKind.GasCan, 1.2, high ? arcFlight(e.y + 1.2, e.aimY, BULLETS[BKind.GasCan].gravity, E.gasFlight) : E.gasFlight);
            e.atkCd = secs(E.gasCd * (e.tactic === ETactic.GasTrapper ? E.gasTrapperMul : 1));
            setState(e, EState.Recover, secs(0.5));
          }
        } else if (e.st === EState.Recover) {
          hold = true;
          if (e.stT >= e.stDur) setState(e, EState.Chase, 0);
        } else setState(e, EState.Chase, 0);
        break;
      }
    }
  }

  if (hold) { wantVx = 0; wantVz = 0; e.vx += (wantVx - e.vx) * 0.25; e.vz += (wantVz - e.vz) * 0.25; }
  if (e.flinchT > 0) { e.vx = 0; e.vz = 0; }

  // separation: robots don't stack
  for (const o of h.enemies) {
    if (o === e || o.dead) continue;
    const sx = e.x - o.x, sz = e.z - o.z;
    const rr = def.radius + ENEMIES[o.type].radius + 0.3;
    const d2 = sx * sx + sz * sz;
    if (d2 < rr * rr && d2 > 1e-6) {
      const d = Math.sqrt(d2);
      const push = (rr - d) * 4;
      e.vx += (sx / d) * push * DT * 8; e.vz += (sz / d) * push * DT * 8;
    }
  }

  // cowboys are solid too (player.ts pushOutOfSolids is the cowboy's side): a robot walking into one slides off
  // it instead of standing in it. A dashing or warping cowboy is passed through.
  for (const p of h.players.values()) {
    if (!isTargetable(p) || p.phase !== 0 || p.move === Move.Special) continue;
    if (p.y >= e.y + def.headY || p.y + 1.8 <= e.y) continue;
    const sx = e.x - p.x, sz = e.z - p.z;
    const rr = def.radius + PLAYER_RADIUS + 0.1;
    const d2 = sx * sx + sz * sz;
    if (d2 < rr * rr && d2 > 1e-6) {
      const d = Math.sqrt(d2);
      const push = (rr - d) * 4;
      e.vx += (sx / d) * push * DT * 8; e.vz += (sz / d) * push * DT * 8;
    }
  }

  // integrate: knockback ignores the edge, walking respects it
  const nx = e.x + (e.vx + e.kx) * DT, nz = e.z + (e.vz + e.kz) * DT;
  e.kx *= 0.88; e.kz *= 0.88;
  const knocked = Math.abs(e.kx) + Math.abs(e.kz) > 1.5;
  const g = h.world.support(nx, nz, e.y + 1);
  if (e.hoverAlt > 0) {
    // the fliers (flying_shooter.gd): the thrust ramps vy toward the hover height over whatever ground is under them;
    // every REST_IN_* seconds the jets go off, they drop to the ground, rest REST_* seconds, and climb again
    if (e.resting) { if (--e.restT <= 0) { e.resting = 0; e.restIn = secs(h.rng.range(REST_IN_MIN, REST_IN_MAX)); } }
    else if (e.restT > 0) { /* descending */ }
    else if (--e.restIn <= 0) { e.restT = secs(h.rng.range(REST_MIN, REST_MAX)); }
    const descending = e.restT > 0 && !e.resting;
    e.x = nx; e.z = nz;
    if (descending) e.vy = Math.max(e.vy - HOVER_THRUST * DT, -REST_DESCENT);
    else { const want = (g === NO_GROUND ? e.y : g) + e.hoverAlt; const climb = Math.max(-HOVER_CLIMB, Math.min(HOVER_CLIMB, (want - e.y) * 1.5)); e.vy += Math.max(-HOVER_THRUST * DT, Math.min(HOVER_THRUST * DT, climb - e.vy)); }
    e.y += e.vy * DT;
    e.airborne = 1;
    if (g !== NO_GROUND && e.y <= g) { e.y = g; e.vy = 0; if (descending) { e.resting = 1; e.airborne = 0; } }
    if (e.y < -45) { h.killEnemy(e, e.lastHitBy); return; }
  } else if (e.airborne) {
    e.x = nx; e.z = nz;
    e.vy -= 24 * DT;
    e.y += e.vy * DT;
    if (g !== NO_GROUND && e.y <= g) { e.y = g; e.vy = 0; e.airborne = 0; }
    if (e.y < -45) { h.killEnemy(e, e.lastHitBy); return; }
  } else if (g === NO_GROUND || g < e.y - 2.5) {
    // a Swarmer stranded on a perch it leapt to hops back down when its cowboy has left
    const dropDown = PERCH_RULES.counters && e.type === EType.Swarmer && e.st === EState.Chase && g !== NO_GROUND && !!t && t.y < e.y - 2.5;
    if (knocked || dropDown) { e.x = nx; e.z = nz; e.airborne = 1; e.vy = 2; }
    else { e.vx *= -0.3; e.vz *= -0.3; }
  } else if (g > e.y + 1.2 || h.world.cliff(e.x, e.z, e.y, nx, nz)) {
    e.vx *= -0.3; e.vz *= -0.3; // too steep, turn back
    if (e.st === EState.Attack) setState(e, EState.Recover, secs(0.8));
  } else {
    e.x = nx; e.z = nz; e.y = g;
  }
  const pos = { x: e.x, y: e.y, z: e.z };
  h.world.pushOut(pos, def.radius * 0.8, def.headY);
  e.x = pos.x; e.z = pos.z;
}

export function recordHistory(e: Enemy, tick: number) {
  const i = tick % HISTORY_TICKS;
  e.hx[i] = e.x; e.hy[i] = e.y; e.hz[i] = e.z; e.hyaw[i] = e.yaw;
}

/** Enemy position at a past (fractional) tick; falls back to now outside the window. */
export function historyAt(e: Enemy, tick: number, now: number, out: { x: number; y: number; z: number; yaw: number }) {
  if (tick >= now || now - tick >= HISTORY_TICKS - 1) { out.x = e.x; out.y = e.y; out.z = e.z; out.yaw = e.yaw; return out; }
  const t0 = Math.floor(tick), f = tick - t0;
  const a = t0 % HISTORY_TICKS, b = (t0 + 1) % HISTORY_TICKS;
  out.x = e.hx[a] + (e.hx[b] - e.hx[a]) * f;
  out.y = e.hy[a] + (e.hy[b] - e.hy[a]) * f;
  out.z = e.hz[a] + (e.hz[b] - e.hz[a]) * f;
  out.yaw = e.hyaw[a];
  return out;
}
